export const mapToDeletion = <T extends object>(item: T) => ({
  ...item,
  _destroy: true,
})

/**
 * Reconciles the result of `getRailsUpdate` by removing redundant
 * destroy + create pairs that represent the same record.
 *
 * This happens when a user removes an item and adds another with the same
 * values — without reconciliation, the payload would send both a `_destroy`
 * and a `create` for the same record, causing unnecessary backend operations.
 *
 * @param railsUpdateResult - Array returned by `getRailsUpdate` (items with `_destroy` + new items)
 * @param accessDestroyedKeys - Extracts comparison keys from a destroyed item (original API shape)
 * @param accessCreatedKeys - Extracts comparison keys from a created item (transformed shape for submission)
 * @returns Reconciled array without the pairs that cancel each other out
 *
 * @example
 * reconcileRailsUpdate(
 *   getRailsUpdate(currentObjectives, formObjectives, ...),
 *   // destroyed item keys (API shape: nested objects)
 *   (destroyed) => [destroyed.system.id, destroyed.part.id, destroyed.action.id],
 *   // created item keys (form shape: flat IDs)
 *   (created) => [created.system_id, created.part_id, created.action_id],
 * )
 */
export const reconcileRailsUpdate = <T extends Record<string, any>>(
  railsUpdateResult: T[],
  accessDestroyedKeys: (destroyed: T) => string[],
  accessCreatedKeys: (created: T) => string[],
) => {
  const destroyedItems = railsUpdateResult.filter((item) => item._destroy)
  const createdItems = railsUpdateResult.filter((item) => !item._destroy)

  const matchedDestroyedIds = new Set<string>()
  const reconciledCreated = createdItems.filter((created) => {
    const createdKeys = accessCreatedKeys(created)
    const match = destroyedItems.find(
      (destroyed) =>
        !matchedDestroyedIds.has(destroyed.id) &&
        accessDestroyedKeys(destroyed).every((key, index) => key === createdKeys[index]),
    )
    if (match) {
      matchedDestroyedIds.add(match.id)
      return false
    }
    return true
  })

  const reconciledDestroyed = destroyedItems.filter((item) => !matchedDestroyedIds.has(item.id))

  return [...reconciledDestroyed, ...reconciledCreated]
}
